import React, { useState } from 'react';
import { fc, mk } from '../../utils';

/**
 * Computes the single highest-priority action for the user right now.
 * Returns null if there's nothing worth surfacing (e.g. no data yet).
 */
function getTopPriority({ dueSoon, budgetRem, budgetPct, inc, vari, varBudget,
  savA, savContrib, cards, dayOfMonth, daysInMonth, today }) {

  // 1 — Bill due TODAY or TOMORROW (highest urgency)
  const urgentBill = dueSoon.find((b) => b.due <= today + 1);
  if (urgentBill) {
    const daysLeft = urgentBill.due - today;
    return {
      em: '⚠️',
      color: 'var(--red)',
      label: daysLeft === 0
        ? `${urgentBill.name} is due TODAY — ${fc(urgentBill.amt)}`
        : `${urgentBill.name} is due TOMORROW — ${fc(urgentBill.amt)}`,
      sub: 'Avoid a late fee and keep your payment history clean.',
      tab: 'money',
      action: 'mark_paid',
      bill: urgentBill,
      btnLabel: 'Mark paid ✓',
    };
  }

  // 2 — Variable budget blown (over 100%)
  if (varBudget > 0 && budgetRem < 0) {
    return {
      em: '🚨',
      color: 'var(--red)',
      label: `You're ${fc(Math.abs(budgetRem))} over your variable budget`,
      sub: `Spent ${fc(vari)} of ${fc(varBudget)} — see where the overrun is.`,
      tab: 'money',
      tabHint: 'money:tracker',
      btnLabel: 'Review spending →',
    };
  }

  // 3 — Budget burning fast (>80% used but <70% through the month)
  const monthPct = (dayOfMonth / daysInMonth) * 100;
  if (varBudget > 0 && parseFloat(budgetPct) > 80 && monthPct < 70) {
    return {
      em: '🔴',
      color: 'var(--ylw)',
      label: `${budgetPct}% of variable budget used — only ${Math.round(monthPct)}% through the month`,
      sub: `${fc(budgetRem)} left for the rest of the month. Time to slow down.`,
      tab: 'money',
      tabHint: 'money:tracker',
      btnLabel: 'Review spending →',
    };
  }

  // 4 — No income logged and it's past day 10
  if (inc === 0 && dayOfMonth > 10) {
    return {
      em: '💰',
      color: 'var(--ylw)',
      label: 'Log your income for this month',
      sub: 'Your dashboard can\'t give you accurate numbers without it.',
      tab: 'money',
      btnLabel: 'Log income →',
    };
  }

  // 5 — Bill due in 2–7 days
  if (dueSoon.length > 0) {
    const next = dueSoon[0];
    const daysLeft = next.due - today;
    return {
      em: '📋',
      color: 'var(--pnk)',
      label: `${next.name} due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — ${fc(next.amt)}`,
      sub: 'Get ahead of it now so it doesn\'t sneak up on you.',
      tab: 'money',
      tabHint: 'money:tracker',
      btnLabel: 'Go to Tracker →',
    };
  }

  // 6 — Savings not started yet, past mid-month
  if (savContrib > 0 && savA === 0 && dayOfMonth > 15) {
    return {
      em: '💵',
      color: 'var(--blu)',
      label: `You haven't logged any savings yet this month`,
      sub: `Your target is ${fc(savContrib)}/mo — even a partial deposit keeps the habit.`,
      tab: 'money',
      btnLabel: 'Log savings →',
    };
  }

  // 7 — High APR debt: use extra budget to chip away
  const highAPR = [...(cards || [])].filter((c) => c.bal > 0).sort((a, b) => b.apr - a.apr)[0];
  if (highAPR && highAPR.apr > 15 && budgetRem > 50) {
    const yearlyInterest = Math.round((budgetRem * highAPR.apr) / 100);
    return {
      em: '🔥',
      color: 'var(--red)',
      label: `Put ${fc(budgetRem)} extra toward ${highAPR.name} this month`,
      sub: `That ${highAPR.apr}% APR costs you ~${fc(yearlyInterest)}/yr. Every extra dollar stops the clock.`,
      tab: 'progress',
      btnLabel: 'View debt plan →',
    };
  }

  // 8 — On track: positive nudge with specific numbers
  if (inc > 0 && varBudget > 0 && budgetRem >= 0) {
    const pctUsed = Math.round((vari / varBudget) * 100);
    return {
      em: '✅',
      color: 'var(--grn)',
      label: `You're on track — ${pctUsed}% of variable budget used`,
      sub: `${fc(budgetRem)} left this month. ${Math.round(monthPct)}% through ${new Date().toLocaleString('default', { month: 'long' })}.`,
      tab: null,
      btnLabel: null,
    };
  }

  return null;
}

export function DailyPriorityCard({ setTab, dueSoon, budgetRem, budgetPct,
  inc, vari, varBudget, savA, savContrib, cards, dayOfMonth, daysInMonth,
  updateState, paidBills, showToast }) {

  const [justPaid, setJustPaid] = useState(null);
  const today = new Date().getDate();

  const priority = getTopPriority({
    dueSoon, budgetRem, budgetPct, inc, vari, varBudget,
    savA, savContrib, cards, dayOfMonth, daysInMonth, today,
  });

  if (!priority) return null;

  const handleAction = () => {
    if (priority.action === 'mark_paid' && priority.bill && updateState) {
      const now = new Date();
      const k = mk(now.getFullYear(), now.getMonth());
      const pb = { ...(paidBills || {}) };
      if (!pb[k]) pb[k] = {};
      pb[k] = { ...pb[k], [priority.bill.id]: priority.bill.amt || true };
      updateState({ paidBills: pb });
      setJustPaid(priority.bill.name);
      showToast?.(`✅ ${priority.bill.name} marked paid`);
      return;
    }
    if (priority.tab) setTab(priority.tab);
  };

  return (
    <div
      style={{
        marginBottom: 14,
        padding: '14px 16px',
        background: 'var(--surf)',
        border: '1px solid var(--bdr)',
        borderLeft: `3.5px solid ${priority.color}`,
        borderRadius: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <span style={{ fontSize: 28, flexShrink: 0, lineHeight: 1 }}>{priority.em}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--txt)', lineHeight: 1.4, marginBottom: 2 }}>
          {priority.label}
        </div>
        <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.4 }}>
          {priority.sub}
        </div>
      </div>
      {priority.btnLabel && (
        <button
          onClick={handleAction}
          disabled={!!justPaid}
          style={{
            flexShrink: 0,
            padding: '7px 13px',
            borderRadius: 9,
            border: 'none',
            background: justPaid ? 'var(--grn)' : priority.color,
            color: (!justPaid && priority.color === 'var(--ylw)') ? '#000' : '#fff',
            fontWeight: 700,
            fontSize: 11,
            cursor: justPaid ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
            transition: 'background .2s',
          }}
        >
          {justPaid ? '✓ Paid!' : priority.btnLabel}
        </button>
      )}
    </div>
  );
}
